import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Icon } from '@ohif/ui';
import { CustomCheckBox } from '@ohif/ui';
import { serviceOptions } from './utils';


export const InviteUserModal = ({
    availableServices,
    cancelInvite,
    confirmInviteUser,
    handleFormChange,
    handleServiceToggle,
    inviting,
    inviteForm,
    showInviteModal,
}) => {
    const { t } = useTranslation();
    const [currentInput, setCurrentInput] = useState('');
    const [inputError, setInputError] = useState('');

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                cancelInvite();
            }
        };

        if (showInviteModal) {
            document.addEventListener('keydown', handleEscape);
        } else {
            setCurrentInput('');
            setInputError('');
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showInviteModal]);

    if (!showInviteModal) return null;

    const emails = Array.isArray(inviteForm?.emails) ? inviteForm.emails : [];

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

    const addEmails = (rawText) => {
        if (!rawText || !rawText.trim()) return;

        const candidates = rawText
            .split(/[\s,;]+/)
            .map(e => e.trim().toLowerCase())
            .filter(Boolean);

        const newValidEmails = [];
        let hasInvalid = false;

        for (const email of candidates) {
            if (isValidEmail(email)) {
                if (!emails.includes(email) && !newValidEmails.includes(email)) {
                    newValidEmails.push(email);
                }
            } else {
                hasInvalid = true;
            }
        }

        if (newValidEmails.length > 0) {
            handleFormChange('emails', [...emails, ...newValidEmails]);
            setCurrentInput('');
            setInputError(hasInvalid ? t('Workspaces:Some invalid emails were ignored') : '');
        } else if (hasInvalid) {
            setInputError(t('Workspaces:Invalid email format'));
        }
    };

    const removeEmail = (emailToRemove) => {
        handleFormChange(
            'emails',
            emails.filter(e => e !== emailToRemove)
        );
    };

    const handleKeyDown = (e) => {
        if (['Enter', ',', ';'].includes(e.key)) {
            e.preventDefault();
            addEmails(currentInput);
        }
    };

    const handlePaste = (e) => {
        const pasteData = e.clipboardData.getData('text');
        if (/[\s,;\n]/.test(pasteData.trim())) {
            e.preventDefault();
            addEmails(pasteData);
        }
    };

    const handleConfirm = () => {
        let finalEmails = [...emails];
        const trimmed = currentInput.trim().toLowerCase();
        if (trimmed && isValidEmail(trimmed) && !finalEmails.includes(trimmed)) {
            finalEmails.push(trimmed);
            handleFormChange('emails', finalEmails);
            setCurrentInput('');
        }
        confirmInviteUser(finalEmails);
    };

    const hasAnyValidEmail = emails.length > 0 || (Boolean(currentInput.trim()) && isValidEmail(currentInput.trim()));
    const canSubmit = !inviting && hasAnyValidEmail && inviteForm.services?.length > 0;

    return (
        <div
            className="fixed inset-0 flex items-center justify-center workspace-modal-backdrop"
            onClick={inviting ? undefined : cancelInvite}
        >
            <div
                className="rounded-2xl p-6 max-w-md w-full mx-4 relative workspace-modal-container workspace-modal-invite"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={cancelInvite}
                    disabled={inviting}
                    className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed workspace-modal-close-btn hover:bg-white/10 hover:text-white hover:scale-105 active:scale-95"
                    aria-label={t('Common:Close')}
                >
                    <Icon className="w-3 h-3" name="close" />
                </button>

                <div className="flex items-center gap-3 mb-5">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl workspace-icon-badge-primary">
                        <Icon className="w-5 h-5" name="user" />
                    </div>
                    <h3 className="text-lg font-bold workspace-text-title">{t('Workspaces:Invite User')}</h3>
                </div>

                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-xs font-semibold uppercase tracking-wider workspace-text-label">
                                {t('Workspaces:User email')}
                            </label>
                            {emails.length > 0 && (
                                <span className="text-xs font-medium workspace-text-accent">
                                    {emails.length} {emails.length > 1 ? (t('Workspaces:emails') || 'emails') : (t('Workspaces:email') || 'email')}
                                </span>
                            )}
                        </div>

                        {emails.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 p-2 mb-2 rounded-xl border border-indigo-500/25 bg-slate-950/40 max-h-32 overflow-y-auto ohif-scrollbar">
                                {emails.map((email) => (
                                    <span
                                        key={email}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-500/15 text-indigo-200 border border-indigo-500/30"
                                    >
                                        <span className="truncate max-w-[190px]">{email}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeEmail(email)}
                                            disabled={inviting}
                                            className="flex items-center justify-center w-3.5 h-3.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/20 transition-all duration-150 cursor-pointer hover:scale-110 active:scale-90 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                                            aria-label={`Remover ${email}`}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={currentInput}
                                onChange={(e) => {
                                    setCurrentInput(e.target.value);
                                    setInputError('');
                                }}
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none transition-all workspace-input-field"
                                placeholder={emails.length > 0 ? t('Workspaces:Add another email') : t('Workspaces:Type email and press Enter')}
                                disabled={inviting}
                            />
                            <button
                                type="button"
                                onClick={() => addEmails(currentInput)}
                                disabled={inviting || !currentInput.trim()}
                                className="px-3.5 py-2 rounded-xl text-sm font-semibold flex items-center justify-center transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed workspace-btn-primary hover:brightness-110 hover:shadow-[0_4px_20px_rgba(99,102,241,0.5)] hover:scale-105 active:scale-95 shrink-0"
                                title={t('Workspaces:Add email')}
                            >
                                <Icon name="icon-plus" className="w-5 h-5" />
                            </button>
                        </div>
                        {inputError && (
                            <p className="text-sm text-red-400 mt-1 flex items-center gap-1">
                                ❌ {inputError}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider mb-2 workspace-text-label">
                            {t('Workspaces:Select services')}
                        </label>
                        <div className="space-y-1.5">
                            {Object.entries(availableServices).map(([key, service]) => (
                                <label
                                    key={key}
                                    className={classnames(
                                        `flex items-center gap-3 p-2.5 rounded-xl transition-all hover:bg-indigo-400/20 workspace-service-option ${inviteForm.services.includes(key) ? 'selected' : ''}`,
                                        {'cursor-not-allowed': inviting, 'cursor-pointer': !inviting}
                                    )}
                                >
                                    <CustomCheckBox
                                        checked={inviteForm.services.includes(key)}
                                        onChange={() => handleServiceToggle(key)}
                                        onClick={() => {}}
                                        ariaLabel={key}
                                        className="w-4 h-4 rounded workspace-checkbox"
                                        disabled={inviting}
                                    />
                                    <img width="24" height="24" src={service.iconPath} />
                                    <span className="text-sm font-medium workspace-text-body">{t(`AdvancedFilters:${service.name}`)}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={cancelInvite}
                        disabled={inviting}
                        className="px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed workspace-btn-cancel hover:bg-white/10 hover:border-white/20 hover:text-white hover:scale-105 active:scale-95"
                    >
                        {t('Workspaces:Cancel')}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!canSubmit}
                        className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed workspace-btn-primary hover:brightness-110 hover:shadow-[0_4px_20px_rgba(99,102,241,0.5)] hover:scale-105 active:scale-95"
                    >
                        {inviting ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />{t('Workspaces:Sending')}...</>
                        ) : t('Workspaces:Invite')}
                    </button>
                </div>
            </div>
        </div>
    );
};


export const DeleteConfirmationModal = ({
    cancelDelete,
    confirmDeleteUser,
    deleting,
    showDeleteModal,
    userToDelete,
}) => {
    const { t } = useTranslation();
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                cancelDelete();
            }
        };

        if (showDeleteModal) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showDeleteModal]);

    if (!showDeleteModal || !userToDelete) return null;

    return (
        <div
            className="fixed inset-0 flex items-center justify-center workspace-modal-backdrop"
            onClick={deleting ? undefined : cancelDelete}
        >
            <div
                className="rounded-2xl p-6 max-w-md w-full mx-4 relative workspace-modal-container workspace-modal-delete"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={cancelDelete}
                    disabled={deleting}
                    className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed workspace-modal-close-btn hover:bg-white/10 hover:text-white hover:scale-105 active:scale-95"
                    aria-label={t('Common:Close')}
                >
                    <Icon className="w-3 h-3" name="close" />
                </button>

                <div className="flex items-center gap-3 mb-5">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl workspace-icon-badge-danger">
                        <Icon className="w-5 h-5" name="alert" />
                    </div>
                    <h3 className="text-lg font-bold workspace-text-title">{t('Workspaces:Confirm Deletion')}</h3>
                </div>

                <p className="text-sm mb-2 workspace-text-muted">
                    {t('Workspaces:Are you sure you want to delete user')} <strong className="workspace-text-title">{userToDelete.username}</strong>
                    &nbsp;{t('Workspaces:of service')} <strong className="workspace-text-title">{t(`AdvancedFilters:${serviceOptions[userToDelete.service].name}`)}</strong>?
                </p>
                <p className="text-sm my-6 text-gray-600">{t('Workspaces:This action cannot be undone')}</p>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={cancelDelete}
                        disabled={deleting}
                        className="px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed workspace-btn-cancel hover:bg-white/10 hover:border-white/20 hover:text-white hover:scale-105 active:scale-95"
                    >
                        {t('Workspaces:Cancel')}
                    </button>
                    <button
                        onClick={confirmDeleteUser}
                        disabled={deleting}
                        className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed workspace-btn-danger hover:brightness-110 hover:shadow-[0_4px_20px_rgba(239,68,68,0.45)] hover:scale-105 active:scale-95"
                    >
                        {deleting ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />{t('Workspaces:Deleting')}...</>) : t('Workspaces:Delete')}
                    </button>
                </div>
            </div>
        </div>
    );
};
